import ApiCore from "./ApiCore";
import {getListOrder, getStoryListFields, getVideoListOrder} from "./apiHelper";

/**
 * All API End-points
 */
class Api {
    api: ApiCore;
    apiToken: string | null;
	constructor(baseUrl = "") {
		this.api = new ApiCore();

		if (baseUrl) {
			this.api.setBaseUrl(baseUrl);
		}

		const urlParams = new URLSearchParams(window.location.search);
		this.apiToken = urlParams.get('auth');
	}

	/**
	 * @param id
	 * @returns {Promise<*>}
	 */
	getDashboard(id: string, versionTest? :boolean) {
		return this.api.get(`/api/bigscreen/${id}/?brandId=${this.api.getBrandId()}${versionTest ? "&version=test" : ""}`, {}, false, this.apiToken || undefined);
	}

	/**
	 *
	 * @param metric
	 * @param brands
	 * @param interval
	 * @param sections
	 * @param tags
	 * @param conversions
	 * @param eventFields
	 * @param isPaid
	 * @param videoFilter
	 * @param pageType
	 * @param referrers
	 * @param referrerTypes
	 * @returns {Promise<*>}
	 */
	getChart(metric: string, brands: string, interval: string, sections: string, tags: string, conversions: string, eventFields: string, isPaid: string, videoFilter: string, pageType: string, referrers: string, referrerTypes: string, includeChannels: string, excludeChannels: string, pubdateInterval: string, userNeeds: string) {
		const brandsArray = brands ? brands : this.api.getBrandId();
		return this.api.get(`/api/brands/graph/${metric}`, {
			brandId: this.api.getBrandId(),
			brands: brandsArray,
			interval,
			pubdateInterval,
			sections,
			eventFields,
			tags,
			conversions,
			isPaid,
			videoFilter,
			pageType,
			referrers,
			referrerTypes,
			includeChannels,
			excludeChannels,
			userNeeds
		}, false, this.apiToken || undefined);
	}

	/**
	 *
	 * @param brandId
	 * @param dayOfTheWeek
	 * @returns {Promise<*>}
	 */
	getChartForechast(brandId: string | null, dayOfTheWeek: number) {
		const brand = brandId ? brandId : this.api.getBrandId();
		return this.api.get(`/api/brands/${brand}/current/pageviews/prognosis?day_of_week=${dayOfTheWeek}`, {}, false, this.apiToken || undefined);
	}

	/**
	 *
	 * @param metric
	 * @param brands
	 * @param interval
	 * @param sections
	 * @param tags
	 * @param conversions
	 * @param eventFields
	 * @param isPaid
	 * @param videoFilter
	 * @param pageType
	 * @param referrers
	 * @param referrerTypes
	 * @param includeChannels
	 * @param excludeChannels
	 * @returns {Promise<*>}
	 */
	getSingleMetric(metric: string, brands: string, interval: number, sections: string, tags: string, conversions: string, eventFields: string, isPaid: string, videoFilter: string, pageType: string, referrers: string, referrerTypes: string, includeChannels: string, excludeChannels: string, pubdateInterval: string, userNeeds: string) {
		const brandsArray = brands ? brands : this.api.getBrandId();
		return this.api.get(`/api/brands/totalMetric/${metric}`, {
			brandId: this.api.getBrandId(),
			brands: brandsArray,
			interval,
			pubdateInterval,
			sections,
			tags,
			conversions,
			eventFields,
			isPaid,
			videoFilter,
			pageType,
			referrers,
			referrerTypes,
			includeChannels,
			excludeChannels,
			userNeeds,
		}, false, this.apiToken || undefined);
	}

	/**
	 *
	 * @param brands
	 * @param fields
	 * @param interval
	 * @param showNotifications
	 * @param notOnHomepage
	 * @param sections
	 * @param tags
	 * @param conversions
	 * @param eventFields
	 * @param order
	 * @param weightedAverage
	 * @param isPaid
	 * @param videoFilter
	 * @param pageType
	 * @param referrers
	 * @param referrerTypes
	 * @param includeChannels
	 * @param excludeChannels
	 * @returns {Promise<*>}
	 */
	getStoryList(brands: string, fields: Array<string>, interval: number, showNotifications: boolean, notOnHomepage: boolean, sections: string, tags: string, conversions: string, eventFields: string, order: string, weightedAverage: boolean, isPaid: string, videoFilter: string, pageType: string, referrers: string, referrerTypes: string, includeChannels: string, excludeChannels: string, pubdateInterval: string, userNeeds: string) {
		const brandsArray = brands ? brands : this.api.getBrandId();
		return this.api.get('/api/brands/story/list', {
			brandId: this.api.getBrandId(),
			brands: brandsArray,
			fields: getStoryListFields(fields),
			interval,
			pubdateInterval,
			showNotifications,
			notOnHomepage,
			sections,
			eventFields,
			tags,
			conversions,
			order: getListOrder(order),
			weightedAverage,
			isPaid,
			videoFilter,
			pageType,
			referrers,
			referrerTypes,
			includeChannels,
			excludeChannels,
			userNeeds,
		}, false, this.apiToken || undefined);
	}

	/**
	 *
	 * @param brands
	 * @param interval
	 * @param sections
	 * @param order
	 * @returns {Promise<*>}
	 */
	getVideoList(brands: string, interval: number, sections: string, tags: string, order: string) {
		const brandsArray = brands ? brands : this.api.getBrandId();
		return this.api.get('/api/brands/video/list', {
			brandId: this.api.getBrandId(),
			brands: brandsArray,
			interval,
			sections,
			tags,
			order: getVideoListOrder(order),
		}, false, this.apiToken || undefined);
	}

	/**
	 *
	 * @param brands
	 * @param interval
	 * @param sections
	 * @param tags
	 * @param type
	 * @param isPaid
	 * @param videoFilter
	 * @param pageType
	 * @param includeChannels
	 * @param excludeChannels
	 * @returns {Promise<*>}
	 */
	getNotifications(brands: string, interval: number, sections: string, tags: string, type: string, isPaid: string, videoFilter: string, pageType: string, includeChannels: string, excludeChannels: string): Promise<any> {
		const brandsArray = brands ? brands : this.api.getBrandId();
		return this.api.get('/api/brands/notification/list', {
			brandId: this.api.getBrandId(),
			brands: brandsArray,
			interval,
			sections,
			tags,
			type,
			isPaid,
			videoFilter,
			pageType,
			includeChannels,
			excludeChannels,
		}, false, this.apiToken || undefined);
	}

	/**
	 *
	 * @returns {Promise<*>}
	 */
	getBrandSettings() {
		return this.api.get(`/api/auth/check/${this.api.getBrandId()}`, {}, false, this.apiToken || undefined);
	}

	/**
	 *
	 * @param id
	 */
	getGoal(id: string) {
		return this.api.get(`/api/brands/${this.api.getBrandId()}/goals/${id}`, {}, false, this.apiToken || undefined);
	}

	/**
	 *
	 * @param id
	 */
	getGoalPrecalculation(intervalFrom = "00:00:00", intervalTo = "23:45:00", metric: string, sectionsArr: string[], conversionsArr: string[]) {
		const sections = (sectionsArr && Array.isArray(sectionsArr)) ? sectionsArr.map(section => section.replace(/^.+-/g, "")).join(',') : '';
		
		if (metric === "conversion" && conversionsArr && Array.isArray(conversionsArr) && conversionsArr.length) {
			const conversions = conversionsArr.join(',');
			return this.api.get(`/api/brands/${this.api.getBrandId()}/goalsPrecalculations/conversion`, {
				intervalFrom,
				intervalTo,
				sections,
				eventNames: conversions,
			}, false, this.apiToken || undefined);
		}

		return this.api.get(`/api/brands/${this.api.getBrandId()}/goalsPrecalculations/${metric}`, {
			intervalFrom,
			intervalTo,
			sections,
		}, false, this.apiToken || undefined);
	}
} // end class

export default Api;
